import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface OrganizationNamePopupProps {
  open: boolean;
  onSubmit: (organizationName: string) => Promise<void>;
}

export default function OrganizationNamePopup({
  open,
  onSubmit,
}: OrganizationNamePopupProps) {
  const [organizationName, setOrganizationName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationName.trim()) {
      setError("Organization name is required");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      await onSubmit(organizationName);
      setOrganizationName("");
    } catch (err) {
      setError("Failed to create organization. Please try again.");
      console.error("Error creating organization:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} modal={true}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Your Organization</DialogTitle>
          <DialogDescription>
            Please provide a name for your organization to continue.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="organization-name" className="col-span-4">
                Organization Name
              </Label>
              <Input
                id="organization-name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className="col-span-4"
                placeholder="Enter your organization name"
                autoFocus
              />
            </div>
            {error && (
              <p className="text-sm text-red-500 col-span-4">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Organization"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
